import {
  Component,
  ElementRef,
  HostListener,
  ViewChild,
  OnInit,
} from '@angular/core';
import { AuthService } from '../../../services/auth.service';
import { RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { HeaderService } from '../../../services/header.service';
import { SettingsModalService } from '../../../services/settings-modal.service';
import { Router, NavigationStart } from '@angular/router';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: true,
  imports: [CommonModule, RouterModule],
})
export class HeaderComponent implements OnInit {
  /**
   * Returns true if the given user is God or Dev role (for viewport widget visibility).
   * @param user The user object (from auth.user$ | async)
   */
  isGodOrDev(user: any): boolean {
    if (!user || !user.email) return false;
    const role = this.auth.getUserRole(user.email);
    return role === 'god' || role === 'dev';
  }

  isToolsOpen = false;

  // Expose environment to template
  get isStaging(): boolean {
    return environment.environment === 'staging';
  }
  // Use data URL for default avatar to avoid asset dependency
  defaultAvatar =
    'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0iI2NjYyI+PHBhdGggZD0iTTEyIDJDNi40OCAyIDIgNi40OCAyIDEyczQuNDggMTAgMTAgMTAgMTAtNC40OCAxMC0xMFMxNy41MiAyIDEyIDJ6bTAgM2MyLjY3IDAgNC44NCAyLjE3IDQuODQgNC44NCAwIDIuNjctMi4xNyA0Ljg0LTQuODQgNC44NC0yLjY3IDAtNC44NC0yLjE3LTQuODQtNC44NCAwLTIuNjcgMi4xNy00Ljg0IDQuODQtNC44NHptMCAxMmM0LjQyIDAgOC4xNyAyLjcyIDkuNyA2LjU0QzIwLjI3IDE5LjMxIDE2LjM4IDIxIDEyIDIxcy04LjI3LTEuNjktMTAuNy00LjQ2YzEuNTMtMy44MiA1LjI4LTYuNTQgOS43LTYuNTR6Ii8+PC9zdmc+';

  // Track failed photo URLs to avoid retrying them
  public failedPhotoUrls = new Set<string>();

  @ViewChild('navbarCollapse', { static: false }) navbarCollapse?: ElementRef;

  @ViewChild('toolsDropdownRef') toolsDropdownRef?: ElementRef;

  constructor(
    public auth: AuthService,
    public header: HeaderService,
    private el: ElementRef,
    private router: Router,
    private settingsModal: SettingsModalService,
  ) {}

  signOut() {
    this.auth.signOut();
  }

  markPhotoUrlAsFailed(url: string | null) {
    if (url) {
      this.failedPhotoUrls.add(url);
    }
  }

  getAvatarUrl(user: any): string {
    if (user?.photoURL) {
      // Ensure we're using HTTPS and add referrerPolicy
      const secureUrl = user.photoURL.replace('http://', 'https://');
      return secureUrl;
    }
    return this.defaultAvatar;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    // Close navbar on outside click
    if (
      this.navbarCollapse &&
      !this.navbarCollapse.nativeElement.contains(event.target as Node)
    ) {
      const button = document.querySelector('.navbar-toggler') as HTMLElement;
      button?.click();
    }

    // Close tools dropdown on outside click
    if (
      this.isToolsOpen &&
      this.toolsDropdownRef &&
      !this.toolsDropdownRef.nativeElement.contains(event.target as Node)
    ) {
      console.log('[Header] Closing dropdown on outside click');
      this.isToolsOpen = false;
    }
  }

  toggleTools(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    console.log('[Header] Toggling dropdown');
    this.isToolsOpen = !this.isToolsOpen;
  }

  ngOnInit() {
    console.log('[Header] Initializing component');
    this.router.events.subscribe((evt) => {
      if (evt instanceof NavigationStart) {
        console.log('[Header] Navigation started, closing dropdown');
        this.isToolsOpen = false;
      }
    });
  }

  // Navigate to a route when clicking nav links
  navigate(path: string) {
    this.router.navigate([path]);
  }

  // Navigate and close dropdown for dropdown menu items
  navigateAndCloseDropdown(path: string, event?: Event) {
    console.log('[Header] navigateAndCloseDropdown called with path:', path);
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      console.log('[Header] Event prevented and stopped');
    }
    this.isToolsOpen = false;
    console.log('[Header] Dropdown closed, navigating to:', path);
    this.router
      .navigate([path])
      .then((success) => {
        console.log('[Header] Navigation result:', success);
      })
      .catch((error) => {
        console.error('[Header] Navigation error:', error);
      });
  }

  // Check if the given path is active
  isActive(path: string): boolean {
    return this.router.url.startsWith(path);
  }

  // Open user settings modal
  navigateToSettings() {
    console.log('[Header] Opening settings modal');
    this.settingsModal.open();
  }
}
